import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/format';
import { computeLinesTotal, round2, unitPrice } from '@/lib/pricing';
import type { LaundryItem, OrderView } from '@/types';

interface OrderAmountSummaryProps {
  order: OrderView;
  items: LaundryItem[];
  currency: string;
}

/**
 * Read-only amount breakdown: approved item prices → items subtotal, plus the
 * recorded delivery fee, ending at the final order total. Lets the laundry see
 * exactly how the total was formed instead of a single opaque number.
 */
export default function OrderAmountSummary({ order, items, currency }: OrderAmountSummaryProps) {
  const { t } = useTranslation();

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const ready = items.length > 0;
  const { total: subtotal, unavailable } = computeLinesTotal(order.items, order.priority, byId);
  const deliveryFee = round2(order.deliveryFee ?? 0);

  const money = (value: number) => (ready ? formatCurrency(value, currency) : '—');

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 shadow-xs">
      <div className="border-b border-background-200 px-4 py-4 md:px-5">
        <h2 className="text-base font-semibold text-foreground-950">{t('orders.amount.title')}</h2>
        <p className="mt-0.5 text-xs text-foreground-500">{t('orders.amount.note')}</p>
      </div>

      <ul className="divide-y divide-background-100">
        {order.items.map((item) => {
          const unit = unitPrice(byId.get(item.itemId), item.serviceType, order.priority);
          const line = unit == null ? null : round2(unit * item.quantity);
          return (
            <li
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground-950">{item.itemName}</p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  {t(`items.service.${item.serviceType}`)} · {item.quantity} {t('orders.detail.units')}
                  {unit != null ? (
                    <span className="ms-1.5 text-foreground-600">
                      ({formatCurrency(unit, currency)} / {t('orders.amount.unitPrice')})
                    </span>
                  ) : null}
                </p>
              </div>
              <span className="whitespace-nowrap text-sm font-semibold text-foreground-900">
                {line == null ? '—' : formatCurrency(line, currency)}
              </span>
            </li>
          );
        })}
      </ul>

      <dl className="space-y-2 border-t border-background-200 bg-background-100/50 px-4 py-4 md:px-5">
        <div className="flex items-center justify-between gap-3 text-sm">
          <dt className="text-foreground-600">{t('orders.amount.subtotal')}</dt>
          <dd className="font-semibold text-foreground-900">{money(subtotal)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm">
          <dt className="text-foreground-600">{t('orders.amount.deliveryFee')}</dt>
          <dd className="font-semibold text-foreground-900">{money(deliveryFee)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-2.5">
          <dt className="text-sm font-semibold text-foreground-950">{t('orders.amount.total')}</dt>
          <dd className="text-lg font-bold text-foreground-950">
            {formatCurrency(order.total, currency)}
          </dd>
        </div>
      </dl>

      {unavailable ? (
        <p className="flex items-start gap-2 border-t border-background-100 px-4 py-3 text-xs leading-relaxed text-amber-700 md:px-5">
          <i className="ri-error-warning-line mt-0.5 text-base text-amber-500" aria-hidden="true" />
          {t('orders.amount.unpriced')}
        </p>
      ) : null}
    </section>
  );
}